import React, { useEffect, useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, ImagePlus, Plus, ChevronLeft, ChevronRight } from "lucide-react";

const Field = ({ label, value, onChange, placeholder, textarea, testid, rows = 3 }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}</span>
    {textarea
      ? <textarea rows={rows} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 w-full rounded-lg border border-slate-200 p-2" />
      : <input value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3" />}
  </label>
);

export const SettingsTab = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const heroRef = useRef(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-slate-400">Loading...</div>;

  const set = (k) => (v) => setS({ ...s, [k]: v });

  const save = async (patch) => {
    setSaving(true);
    try { const r = await api.put("/admin/settings", { ...s, ...patch }); setS(r.data); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      let latest = s;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/admin/settings/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
        latest = r.data;
      }
      setS(latest);
      toast.success(`${files.length} image(s) uploaded`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadLogo = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS(r.data);
      toast.success("Logo updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Logo upload failed"); }
    setLogoUploading(false);
    if (logoRef.current) logoRef.current.value = "";
  };

  const uploadHero = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setHeroUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/settings/hero", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS(r.data);
      toast.success("Hero image updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Image upload failed"); }
    setHeroUploading(false);
    if (heroRef.current) heroRef.current.value = "";
  };

  const removeImage = async (id) => {
    try { const r = await api.delete(`/admin/settings/gallery/${id}`); setS(r.data); toast.success("Image removed"); }
    catch { toast.error("Could not remove image"); }
  };

  const updateCaption = async (id, caption) => {
    try { const r = await api.put(`/admin/settings/gallery/${id}`, { caption }); setS(r.data); }
    catch { toast.error("Could not save caption"); }
  };

  const moveImage = async (id, dir) => {
    const arr = [...(s.opening_soon_gallery || [])];
    const idx = arr.findIndex((g) => g.id === id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    try { const r = await api.post("/admin/settings/gallery/reorder", { order: arr.map((g) => g.id) }); setS(r.data); }
    catch { toast.error("Could not reorder"); }
  };

  const gallery = s.opening_soon_gallery || [];
  const branches = s.branches || [];
  const setBranch = (id, k, v) => setS({ ...s, branches: branches.map((b) => (b.id === id ? { ...b, [k]: v } : b)) });
  const addBranch = () => setS({ ...s, branches: [...branches, { id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())), name: "", address: "", hours: "", phone: "" }] });
  const removeBranch = (id) => setS({ ...s, branches: branches.filter((b) => b.id !== id) });

  return (
    <div className="space-y-4" data-testid="settings-tab">
      <div className="font-heading text-2xl text-brand-navy">Settings</div>

      {/* Branding */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="branding-manager">
        <div className="font-heading text-lg text-brand-navy">Logo &amp; branding</div>
        <p className="text-sm text-slate-600">Upload your business logo. It appears in the site header, footer and Opening Soon page.</p>
        <div className="mt-4 flex items-center gap-5 flex-wrap">
          <div className="h-20 w-40 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-center overflow-hidden">
            {s.logo_url ? <img src={mediaUrl(s.logo_url)} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" /> : <span className="text-slate-400 text-xs">No logo</span>}
          </div>
          <input ref={logoRef} type="file" accept="image/*" hidden onChange={uploadLogo} data-testid="logo-file-input" />
          <button data-testid="logo-upload-btn" disabled={logoUploading} onClick={() => logoRef.current?.click()}
            className="h-10 px-5 rounded-full font-bold text-sm bg-brand-green text-white inline-flex items-center gap-2 disabled:opacity-60">
            <Upload className="h-4 w-4" /> {logoUploading ? "Uploading..." : "Upload logo"}
          </button>
        </div>
      </div>

      {/* Business profile */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="business-manager">
        <div className="font-heading text-lg text-brand-navy">Business profile</div>
        <p className="text-sm text-slate-600">Shown on the Contact page and used across the site.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <Field label="Business name" value={s.business_name} onChange={set("business_name")} testid="setting-business_name" />
          <Field label="Tagline" value={s.business_tagline} onChange={set("business_tagline")} testid="setting-business_tagline" />
          <Field label="Contact email" value={s.business_email} onChange={set("business_email")} testid="setting-business_email" />
          <Field label="Contact phone" value={s.business_phone} onChange={set("business_phone")} placeholder="e.g. (613) 555-0199" testid="setting-business_phone" />
          <Field label="Main address" value={s.business_address} onChange={set("business_address")} testid="setting-business_address" />
          <Field label="Hours of operation" value={s.hours_of_operation} onChange={set("hours_of_operation")} placeholder="Mon–Fri 9–6, Sat 10–4" textarea testid="setting-hours_of_operation" />
        </div>
        <button data-testid="business-save-btn" disabled={saving} onClick={() => save({})} className="mt-4 bg-brand-navy text-white rounded-full px-6 h-11 font-bold disabled:opacity-60">Save profile</button>
      </div>

      {/* Storefront highlight & Ask DealsWagon chat */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="highlight-chat-manager">
        <div className="font-heading text-lg text-brand-navy">Home highlight &amp; Ask DealsWagon chat</div>
        <p className="text-sm text-slate-600">The home page shows a live "items available" count with your quality line. The chat helps shoppers from your live catalog and can hand off to WhatsApp.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <Field label="Quality tagline (home highlight)" value={s.availability_tagline} onChange={set("availability_tagline")} placeholder="All brand new / mint condition only" testid="setting-availability_tagline" />
          <Field label="WhatsApp number (for chat handoff)" value={s.whatsapp_number} onChange={set("whatsapp_number")} placeholder="+1 613 555 0199" testid="setting-whatsapp_number" />
        </div>
        <div className="mt-3">
          <Field label="Store info the chat shares (location, pickup, discounts, website)" value={s.chat_intro} onChange={set("chat_intro")} textarea rows={8} testid="setting-chat_intro" />
        </div>
        <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-slate-600">Show the "Ask DealsWagon" chat bubble on shopper pages.</div>
          <button data-testid="toggle-chat-enabled" disabled={saving} onClick={() => save({ chat_enabled: !s.chat_enabled })}
            className={`h-10 px-5 rounded-full font-bold text-sm ${s.chat_enabled ? "bg-brand-green text-white" : "bg-slate-200 text-slate-700"}`}>
            {s.chat_enabled ? "ON — chat visible" : "OFF — chat hidden"}
          </button>
        </div>
        <button data-testid="highlight-chat-save-btn" disabled={saving} onClick={() => save({})} className="mt-4 bg-brand-navy text-white rounded-full px-6 h-11 font-bold disabled:opacity-60">Save</button>
      </div>

      {/* Branches */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="branches-manager">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-heading text-lg text-brand-navy">Branches / locations</div>
            <p className="text-sm text-slate-600">Add each store or pickup location with its own address and hours.</p>
          </div>
          <button onClick={addBranch} data-testid="branch-add-btn" className="h-10 px-5 rounded-full font-bold text-sm bg-brand-red text-white inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Add branch</button>
        </div>
        <div className="mt-4 space-y-3">
          {branches.length === 0 ? <div className="text-slate-400 text-sm">No branches added yet.</div> :
            branches.map((b) => (
              <div key={b.id} className="border border-slate-200 rounded-xl p-3" data-testid={`branch-${b.id}`}>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Branch name" value={b.name} onChange={(v) => setBranch(b.id, "name", v)} testid={`branch-name-${b.id}`} />
                  <Field label="Phone" value={b.phone} onChange={(v) => setBranch(b.id, "phone", v)} testid={`branch-phone-${b.id}`} />
                  <Field label="Address" value={b.address} onChange={(v) => setBranch(b.id, "address", v)} testid={`branch-address-${b.id}`} />
                  <Field label="Hours" value={b.hours} onChange={(v) => setBranch(b.id, "hours", v)} testid={`branch-hours-${b.id}`} />
                </div>
                <button onClick={() => removeBranch(b.id)} data-testid={`branch-remove-${b.id}`} className="mt-2 text-brand-red inline-flex items-center gap-1 text-sm font-bold"><Trash2 className="h-4 w-4" /> Remove</button>
              </div>
            ))}
        </div>
        <button data-testid="branches-save-btn" disabled={saving} onClick={() => save({})} className="mt-4 bg-brand-navy text-white rounded-full px-6 h-11 font-bold disabled:opacity-60">Save branches</button>
      </div>

      {/* Opening Soon */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-heading text-lg text-brand-navy">"Opening Soon" homepage</div>
            <p className="text-sm text-slate-600">When on, the whole site shows the opening-soon landing page. Turn off to open the full store.</p>
          </div>
          <button data-testid="toggle-opening-soon" disabled={saving} onClick={() => save({ opening_soon_enabled: !s.opening_soon_enabled })}
            className={`h-10 px-5 rounded-full font-bold text-sm ${s.opening_soon_enabled ? "bg-brand-green text-white" : "bg-slate-200 text-slate-700"}`}>
            {s.opening_soon_enabled ? "ON — showing Opening Soon" : "OFF — showing store homepage"}
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <Field label="Headline badge" value={s.opening_soon_headline} onChange={set("opening_soon_headline")} testid="setting-opening_soon_headline" />
          <Field label="Opening date text (optional)" value={s.opening_soon_date_text} onChange={set("opening_soon_date_text")} testid="setting-opening_soon_date_text" />
          <Field label="Address / pickup location line" value={s.opening_soon_location} onChange={set("opening_soon_location")} testid="setting-opening_soon_location" />
          <Field label="Location heading (above address)" value={s.opening_soon_location_label} onChange={set("opening_soon_location_label")} testid="setting-opening_soon_location_label" />
          <div>
            <Field label="Hero image URL" value={s.opening_soon_image} onChange={set("opening_soon_image")} testid="setting-opening_soon_image" />
            <div className="mt-2 flex items-center gap-3">
              <div className="h-14 w-24 bg-slate-50 border border-slate-200 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                {s.opening_soon_image ? <img src={mediaUrl(s.opening_soon_image)} alt="Hero" className="max-h-full max-w-full object-contain" data-testid="hero-preview" /> : <span className="text-slate-400 text-[10px]">No image</span>}
              </div>
              <input ref={heroRef} type="file" accept="image/*" hidden onChange={uploadHero} data-testid="hero-file-input" />
              <button type="button" data-testid="hero-upload-btn" disabled={heroUploading} onClick={() => heroRef.current?.click()}
                className="h-9 px-4 rounded-full font-bold text-sm bg-brand-green text-white inline-flex items-center gap-2 disabled:opacity-60">
                <Upload className="h-4 w-4" /> {heroUploading ? "Uploading..." : "Upload image"}
              </button>
            </div>
          </div>
        </div>
        <div className="mt-3"><Field label="Message" value={s.opening_soon_message} onChange={set("opening_soon_message")} textarea testid="setting-opening_soon_message" /></div>
        <button data-testid="settings-save-btn" disabled={saving} onClick={() => save({})} className="mt-4 bg-brand-navy text-white rounded-full px-6 h-11 font-bold disabled:opacity-60">Save text</button>
      </div>

      {/* Gallery */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="gallery-manager">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-heading text-lg text-brand-navy">Opening Soon photo gallery</div>
            <p className="text-sm text-slate-600">Upload photos to showcase on the Opening Soon page. JPG, PNG, WEBP or GIF up to 10MB each.</p>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={uploadFiles} data-testid="gallery-file-input" />
          <button data-testid="gallery-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
            className="h-10 px-5 rounded-full font-bold text-sm bg-brand-green text-white inline-flex items-center gap-2 disabled:opacity-60">
            <Upload className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload photos"}
          </button>
        </div>
        {gallery.length === 0 ? (
          <div className="mt-5 border-2 border-dashed border-slate-200 rounded-xl p-10 text-center text-slate-400" data-testid="gallery-empty">
            <ImagePlus className="h-8 w-8 mx-auto mb-2" />
            No photos yet. Upload images to build the gallery.
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="gallery-grid">
            <div className="text-sm text-slate-600 mb-3">Hover a photo to reorder (arrows) or remove. The order here is the order shown on the Opening Soon page.</div>
            {gallery.map((img, idx) => (
              <div key={img.id} className="rounded-xl overflow-hidden border border-slate-200 bg-white" data-testid={`gallery-item-${img.id}`}>
                <div className="relative group aspect-square">
                  <img src={mediaUrl(img.url)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImage(img.id)} data-testid={`gallery-remove-${img.id}`}
                    className="absolute top-2 right-2 bg-brand-red text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => moveImage(img.id, -1)} disabled={idx === 0} data-testid={`gallery-moveleft-${img.id}`}
                      className="bg-white/90 text-brand-navy rounded-full p-1.5 shadow disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
                    <button onClick={() => moveImage(img.id, 1)} disabled={idx === gallery.length - 1} data-testid={`gallery-moveright-${img.id}`}
                      className="bg-white/90 text-brand-navy rounded-full p-1.5 shadow disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
                  </div>
                </div>
                <input defaultValue={img.caption || ""} placeholder="Add a caption..."
                  onBlur={(e) => { if ((e.target.value || "") !== (img.caption || "")) updateCaption(img.id, e.target.value); }}
                  data-testid={`gallery-caption-${img.id}`}
                  className="w-full text-xs px-2 py-2 border-t border-slate-200 focus:outline-none focus:bg-slate-50" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Social */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="social-manager">
        <div className="font-heading text-lg text-brand-navy">Social media links</div>
        <p className="text-sm text-slate-600">Add your profile URLs. Links appear in the site footer and on the Opening Soon page. Leave blank to hide.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <Field label="Facebook URL" value={s.social_facebook} onChange={set("social_facebook")} placeholder="https://facebook.com/..." testid="setting-social_facebook" />
          <Field label="Instagram URL" value={s.social_instagram} onChange={set("social_instagram")} placeholder="https://instagram.com/..." testid="setting-social_instagram" />
          <Field label="YouTube URL" value={s.social_youtube} onChange={set("social_youtube")} placeholder="https://youtube.com/@..." testid="setting-social_youtube" />
          <Field label="TikTok URL" value={s.social_tiktok} onChange={set("social_tiktok")} placeholder="https://tiktok.com/@..." testid="setting-social_tiktok" />
          <Field label="X (Twitter) URL" value={s.social_x} onChange={set("social_x")} placeholder="https://x.com/..." testid="setting-social_x" />
        </div>
        <button data-testid="social-save-btn" disabled={saving} onClick={() => save({})} className="mt-4 bg-brand-navy text-white rounded-full px-6 h-11 font-bold disabled:opacity-60">Save links</button>
      </div>
    </div>
  );
};
